import { SerializedStyles } from '@emotion/react';
import React from 'react';
import PropsComponent from '../../common-components/component-library/types/PropsComponent';
import { breakpointQueries } from '../styleguide';

const IMAGE_WIDTHS = [80, 200, 400, 800, 1200, 1600, 4000];

type Device = 'desktop' | 'tablet' | 'mobile';
export type DeviceSpec = { desktop?: number; tablet?: number; mobile?: number };
const DEVICE_KEYS: Device[] = ['desktop', 'tablet', 'mobile'];

export type ImageFit = 'pad' | 'fill' | 'scale' | 'crop' | 'thumb';

type ImageParams = {
  url: string;
  fileName: string;
  spans?: DeviceSpec;
  aspectRatios?: DeviceSpec;
  fit?: ImageFit;
  maxWidth?: number;
};

type ImageProps = {
  alt?: string;
  className?: string;
  css?: SerializedStyles;
};

export const getPicture = ({
  url,
  fileName,
  spans,
  aspectRatios,
  fit,
  maxWidth,
}: ImageParams): PropsComponent<ImageProps> => {
  const fileExt = fileName.split('.').pop();
  if (fileExt && ['svg'].includes(fileExt)) {
    const SimpleImage = ({ alt, className }: ImageProps) => (
      <img src={url} alt={alt} className={className} />
    );
    return SimpleImage;
  }
  const sizes = getSizes({ spans, maxWidth });
  const webpSources = getSourceTag({
    url,
    query: 'q=80',
    type: 'webp',
    sizes,
    aspectRatios,
    fit,
  });
  const { defaultType, defaultQuery, imgSrc } = getDefaultSetting(fileExt, url);
  const noWebpSource = getSourceTag({
    url,
    query: defaultQuery,
    type: defaultType,
    sizes,
    aspectRatios,
    fit,
  });

  const Picture = ({ alt, className, css }: ImageProps) => {
    const image = (
      <img
        srcSet={getSrcSet({
          url,
          query: defaultQuery,
          type: defaultType,
          aspectRatio: aspectRatios?.mobile,
        })}
        src={imgSrc}
        alt={alt}
        className={className}
        css={css}
        sizes={sizes}
      />
    );
    return (
      <>
        <picture style={{ width: '100%', height: '100%' }} css={css}>
          {webpSources}
          {noWebpSource}
          {image}
        </picture>
        <noscript>{image}</noscript>
      </>
    );
  };
  return Picture;
};

type Params = {
  url: string;
  query: string;
  type: string;
  fit?: ImageFit;
  maxWidth?: number;
};

const getSourceTag = ({
  url,
  query,
  type,
  sizes,
  aspectRatios,
  fit,
}: Params & { sizes: string; aspectRatios?: DeviceSpec }) => {
  const mobileSource = (
    <source type={`image/${type}`} srcSet={getSrcSet({ url, query, type, fit })} sizes={sizes} />
  );
  if (!aspectRatios) {
    return mobileSource;
  }
  return DEVICE_KEYS.map((device) => {
    if (device === 'mobile') {
      return (
        <source
          key={device}
          type={`image/${type}`}
          srcSet={getSrcSet({ url, query, type, aspectRatio: aspectRatios[device], fit })}
          sizes={sizes}
        />
      );
    }
    const media = breakpointQueries[device];
    return (
      <source
        media={media}
        key={device}
        type={`image/${type}`}
        srcSet={getSrcSet({ url, query, type, aspectRatio: aspectRatios[device] })}
        sizes={sizes}
      />
    );
  });
};

const getSrcSet = ({
  url,
  query,
  type,
  aspectRatio = 1,
  fit,
}: Params & {
  aspectRatio?: number;
}) => {
  const srcSets = IMAGE_WIDTHS.map((width) => {
    const height = Math.floor(width / aspectRatio);
    // Scaling factor if image width/height is bigger than 4000.
    // If it's not it will be 1
    const ratioFromMax = 4000 / Math.max(width, height, 4000);
    const [w, h] = [Math.floor(width * ratioFromMax), Math.floor(height * ratioFromMax)];
    const fitted = fit ? `&fit=${fit}` : '';
    return `${url}?fm=${type}&${query}&w=${w}&h=${h}${fitted} ${width}w`;
  });
  return srcSets.join(',');
};

const getSizes = ({ spans, maxWidth }: { spans?: DeviceSpec; maxWidth?: number }) => {
  if (!spans) {
    return getVwMaxWidth({ maxWidth });
  }
  return DEVICE_KEYS.map((device) => {
    const vw = getVwMaxWidth({ span: spans[device], maxWidth });
    if (device === 'mobile') {
      return vw;
    }
    return `${breakpointQueries[device]} ${vw}`;
  }).join(',');
};

const getVwMaxWidth = ({ span, maxWidth }: { span?: number; maxWidth?: number }) => {
  const vw = getVw({ span });
  return maxWidth ? `min(${maxWidth}px, ${vw})` : vw;
};

const getVw = ({ span }: { span?: number }) => {
  if (!span) {
    return '100vw';
  }
  return `${Math.floor(100 / span)}vw`;
};

const getDefaultSetting = (
  fileExt: string | undefined,
  url: string,
): { defaultType: string; defaultQuery: string; imgSrc: string } => {
  // support transparent background for browsers that don't support webp
  return fileExt && ['png'].includes(fileExt)
    ? {
        defaultType: 'png',
        defaultQuery: 'q=90',
        imgSrc: `${url ? url : ''}?q=90`,
      }
    : {
        defaultType: 'jpg',
        defaultQuery: 'fm=jpg&fl=progressive&q=90',
        imgSrc: `${url ? url : ''}?fm=jpg&fl=progressive&q=90`,
      };
};
